import "@react-sigma/core/lib/react-sigma.min.css";
import './ContentGraph.scss';

import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { SigmaContainer, useLoadGraph } from "@react-sigma/core";
import { AppContext } from 'components/AppContext/AppContext';
import NavHeading, { NavHeadingProps } from 'components/NavHeading/NavHeading';
import { NavHeadings } from 'constants/Constants';
import { useDataSet } from 'customHooks/useDataSet';
import { MultiDirectedGraph } from "graphology";
import forceLayout from 'graphology-layout-force';
import DataSetOperators, { SearchType } from 'interfaces/DataSetOperators';
import { Entities } from 'models/Entities';
import { ErrorCause } from "models/ErrorResponse";
import { GraphDataset } from 'models/GraphDataset';
import { Alert, Form, InputGroup, Row } from 'react-bootstrap';

import { drawHover } from './canvas-utils';
import GraphEventsController from './GraphEventsController';
import GraphSettingsController from './GraphSettingsController';

const sigmaStyle = { height: "100vh", width: "100vw" };

const CURRENT_TAB_NAME = 'graph';
const MAX_GRAPH_SIZE = 50000;

interface LoadGraphProps {
  graph: GraphDataset,
  onLoading: () => void;
  onComplete: () => void;
  activeTab: string;
}

const LoadGraph: FC<LoadGraphProps> = ({ graph, onLoading, onComplete, activeTab }) => {
  const loadGraph = useLoadGraph();

  useEffect(() => {

    if (activeTab !== CURRENT_TAB_NAME)
      return;

    if (onLoading) {
      onLoading();
    }

    const graphChart = new MultiDirectedGraph();

    graphChart.clear();

    // eslint-disable-next-line unused-imports/no-unused-vars
    const positions = forceLayout(graphChart, {
      maxIterations: 50,
      settings: {
        attraction: 100,
        gravity: 100000,
        maxMove: 0,
        inertia: 0
      }
    });

    const { nodes, relationships } = graph;

    nodes.forEach(n => {
      const size = (n.properties as any).size + 7;
      graphChart.addNode(n.id, { x: Math.random(), y: Math.random(), label: n.labels[0], size, properties: n.properties });
    });

    relationships.forEach(r => {
      graphChart.addEdgeWithKey(r.id, r.startNode, r.endNode, { label: r.id });
    });

    loadGraph(graphChart, true);

    forceLayout.assign(graphChart, 50);

    if (onComplete) {
      onComplete();
    }

  }, [loadGraph, graph, activeTab]);

  return null;
};

interface ContentGraphProps {
  activeTab: string;
}

const ContentGraph: FC<ContentGraphProps> = ({ activeTab }) => {
  const { configRepo, selectedProjectId, setIsProjectSelectable } = useContext(AppContext);
  const initialParams: DataSetOperators = {
    Top: MAX_GRAPH_SIZE
  };
  const { dataSet, setOperators, operators } = useDataSet<GraphDataset>(
    configRepo!.CRAWLER_API_BASE_URI,
    Entities.ContentGraph,
    selectedProjectId,
    initialParams,
  );
  const defaultGraph: GraphDataset = {
    nodes: [{
      id: 0,
      labels: [],
      properties: {}
    }],
    relationships: [{
      id: 0,
      type: '',
      startNode: 0,
      endNode: 0,
      properties: {},
      source: 0,
      target: 0,
      linknum: 0
    }]
  };
  const [graph, setGraph] = useState(defaultGraph);

  const hasWebGL = typeof WebGLRenderingContext !== 'undefined';

  useEffect(() => {
    if (!dataSet.isLoading) {
      if (dataSet.isError) {
        setGraph(defaultGraph);
      } else {
        setGraph(dataSet.responseData as any as GraphDataset);
      }
    }
  }, [dataSet]);

  useEffect(() => {
    if (activeTab === CURRENT_TAB_NAME &&
      dataSet.isError &&
      dataSet.error?.cause === ErrorCause.NotFoundError) {
      const ops = { ...initialParams };
      ops.ProjectId = selectedProjectId;
      setOperators(ops);
    }
  }, [activeTab, dataSet.isError, dataSet.error?.cause]);

  const [filterText, setFilterText] = React.useState('');

  const onSearch = (e: React.FormEvent<HTMLButtonElement>, text: string) => {
    e.preventDefault();
    const ops = { ...operators };
    ops.SearchProperty = 'Title|Url|MetaDescription|MetaKeywords';
    ops.SearchType = SearchType.Contains;
    ops.SearchValue = text;
    ops.ProjectId = selectedProjectId;
    setOperators(ops);
  };

  useEffect(() => {
    setIsProjectSelectable(true);
  }, []);

  useEffect(() => {
    const listener = (event: any) => {
      if (event.code === "Enter" || event.code === "NumpadEnter") {
        onSearch(event, filterText);
      }
    };
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, [filterText]);

  useEffect(() => {
    if (operators.ProjectId !== selectedProjectId) {
      const ops = { ...operators };
      ops.ProjectId = selectedProjectId;
      setOperators(ops);
    }
  }, [selectedProjectId, operators]);

  const onSearchTextChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value);
  };

  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const [isRendering, setIsRendering] = useState<boolean>(true);
  const onLoading = () => {
    setIsRendering(true);
  };
  const onComplete = () => {
    setIsRendering(false);
  }

  if (!hasWebGL) {
    return (<div />);
  }

  if (dataSet?.isLoading) {
    return (<Row className="content-graph container">
      <h1>Content Graph</h1>
      <Alert variant='info'>
        Loading... please be patient while we analyze your content graph.
      </Alert>
    </Row>);
  }

  if (dataSet?.isError) {

    if ((dataSet.error as any).message?.includes('404')) {
      return (<Row className="content-graph container">
        <h1>Content Graph</h1>
        <Alert variant='danger'>
          <strong>Error</strong>: You have not configured your sitemap URL or the website crawler has not completed yet.
          Please go to <a href="/seo">SEO Setup</a> to ingest your website data.
        </Alert>
      </Row>);
    }

    return (<Row className="content-graph container">
      <h1>Content Graph</h1>
      <Alert variant='info'>
        <strong>Error</strong>: Failed to load content graph.  Please contact support.
      </Alert>
    </Row>);

  }

  if (!graph) {
    return (<Row className="content-graph container">
      <h1>Content Graph</h1>
      <Alert variant='info'>
        Loading... please be patient while we analyze your content graph.
      </Alert>
    </Row>);
  }

  const heading: NavHeadingProps = {
    title: 'Content Graph',
    ...NavHeadings.ContentGraph,
  };


  return (
    <div className='content-graph container'>
      <NavHeading {...heading} />
      <Form id="contentGraphSearchForm" className='mb-3 row' onSubmit={(e: React.FormEvent<HTMLFormElement>) => { onSearch(e as any, filterText); }}>
        <InputGroup id="searchTerms">
          <Form.Control type='text'
            id="tbxSearchGraph"
            placeholder='Enter search terms here to filter the graph'
            value={filterText}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter') {
                onSearch(event as any, filterText);
              }
            }}
            onChange={onSearchTextChanged} />
          <InputGroup.Text>
            <button type='submit'
              form="contentGraphSearchForm"
              id="btnSearchGraph"
              disabled={dataSet.isLoading}
              onClick={(e) => { onSearch(e, filterText); }}
              className="btn btn-primary">
              {dataSet.isLoading ?
                <>Loading...</> :
                <><i className='bi bi-search' /> {" "}Search</>
              }
            </button>
          </InputGroup.Text>
        </InputGroup>
      </Form>

      <Row style={{ position: 'relative' }}>
        {(dataSet.isLoading || isRendering) ?
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div> : ""
        }
        <SigmaContainer
          id="graphContainer"
          style={sigmaStyle}
          settings={{
            allowInvalidContainer: true,
            renderLabels: false,
            defaultDrawNodeHover: drawHover
          }}
          graph={MultiDirectedGraph}>
          <GraphSettingsController hoveredNode={hoveredNode} />
          <GraphEventsController setHoveredNode={setHoveredNode} />
          <LoadGraph graph={graph as unknown as GraphDataset} onLoading={onLoading} onComplete={onComplete} activeTab={activeTab} />
        </SigmaContainer>
      </Row>
    </div>
  );
}

export default ContentGraph;
